import { CellValueChangedEvent, StatusPanelDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo } from 'react';

import { useGetLogs, useLogActions } from 'bom/hooks';
import { ILog } from 'bom/types';
import { AgGridTable } from 'core/components';
import { NotificationPosition, SortDirection, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow, ISelectionActionProps } from 'core/types';
import { IJob } from 'jobs/types';

import { columnDef } from './columns';
import { TotalsStatusBar } from './TotalsStatusBar';
import { RemoveLogsButton } from './UpsertLog';
import { AddLogButton } from './UpsertLogs';

interface IProps {
  job: IJob;
}

export const LogList = ({ job }: IProps) => {
  const { upsertLog } = useLogActions();

  const columnDefs = useMemo(() => columnDef(), []);

  const initialState = useMemo(
    () => ({
      sort: {
        sortModel: [{ colId: 'activityDate', sort: SortDirection.DESC }],
      },
    }),
    []
  );

  const payload = useMemo(
    () => ({
      filters: { bomId: job.id },
    }),
    [job.id]
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetLogs(payload, { fetchAllPages: true });

  const isDataLoadCompleted = useMemo(() => loadingPercentage === 100, [loadingPercentage]);

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(
    () => ({
      statusPanels: [
        {
          statusPanel: TotalsStatusBar,
          align: 'left',
        },
      ],
    }),
    []
  );

  const actionButtons = useCallback(() => {
    return [<AddLogButton key="addLogButton" jobId={job.id} />];
  }, [job.id]);

  const selectionActionButtons = useCallback(
    ({ selectedRows }: ISelectionActionProps) => [
      <RemoveLogsButton
        key="removeLogsButton"
        logs={selectedRows as ILog[]}
        notificationPosition={NotificationPosition.TOP}
      />,
    ],
    []
  );

  const onCellValueChanged = useCallback(
    async (
      _: AgGridReact,
      event: CellValueChangedEvent
    ): Promise<{ updatedRow?: AgGridDataRow }> => {
      const {
        success,
        bomLog: updatedLog,
        error,
      } = await upsertLog({
        bomLog: {
          id: event.data.id,
          [event.colDef.field]: event.newValue,
        },
      });

      if (!success) {
        throw new Error(error);
      }

      return { updatedRow: updatedLog };
    },
    [upsertLog]
  );

  return (
    <AgGridClientTableProvider
      tableId="jobLogListTable"
      entityType="TimeLog"
      layoutType={TableLayoutType.JOB_LOG}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      initialState={initialState}
      onCellValueChanged={onCellValueChanged}
      headerCheckbox={isDataLoadCompleted}
      hasLayoutActions
      statusBar={statusBar}
    >
      <AgGridTable actionButtons={actionButtons} selectionActionButtons={selectionActionButtons} />
    </AgGridClientTableProvider>
  );
};
